<template>
    <section class="NddOrgsBlock py-14 sm:py-20">
        <div class="container">
            <slot name="title">
                <CHeading class="mb-4" tag="h2">Podpora organizácií a dobrovoľníkov</CHeading>
            </slot>

            <slot name="description">
                <CText class="mb-8">
                    Každú organizáciu, ktorá zverejní svoju prvú dobrovoľnícku príležitosť v národnej databáze, bude
                    kontaktovať príslušné krajské dobrovoľnícke centrum. Košický, bratislavský kraj a zahraničné ponuky
                    pokrýva Platforma dobrovoľníckych centier a organizácií. Poslaním dobrovoľníckych centier je
                    vytváranie prostredia pre rozvoj dobrovoľníctva. Poskytujú vzdelávanie, konzultácie a podporu pri
                    zavádzaní a riadení dobrovoľníckeho programu. Ak máte otázky k práci s dobrovoľníkmi, neváhajte ich
                    kontaktovať.
                </CText>
            </slot>

            <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <div
                    v-for="(item, index) in items"
                    :key="index"
                    class="group relative flex min-w-0 flex-col items-baseline gap-4 rounded-md border border-solid border-white/25 bg-white p-4 shadow-card-box transition duration-500 ease-in-out hover:bg-primary-500 dark:border-default dark:bg-surface-card"
                >
                    <CHeading class="mb-0 w-full break-words text-lg lg:text-2xl" variant="3">
                        <span class="block transition duration-500 ease-in-out group-hover:text-white">
                            {{ item.title }}
                        </span>
                    </CHeading>
                    <div class="flex min-w-0 w-full flex-col">
                        <span
                            v-if="item.name"
                            class="break-words font-medium transition duration-500 ease-in-out group-hover:text-white"
                        >
                            {{ item.name }}
                        </span>
                        <div
                            v-if="item.email"
                            class="flex min-w-0 items-center gap-2 text-gray-600 dark:text-txt-body transition duration-500 ease-in-out group-hover:text-white/90"
                        >
                            <svg
                                aria-hidden="true"
                                class="h-3.5 w-3.5 shrink-0"
                                fill="none"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.8"
                                viewBox="0 0 20 20"
                            >
                                <path d="M4 6h16v12H4z" />
                                <path d="m4 7 8 6 8-6" />
                            </svg>
                            <a
                                class="block min-w-0 flex-1 break-all no-underline underline-offset-2 hover:underline"
                                :href="`mailto:${item.email}`"
                            >
                                {{ item.email }}
                            </a>
                        </div>
                        <div
                            v-if="item.phone"
                            class="flex min-w-0 items-center gap-2 text-gray-600 dark:text-txt-body transition duration-500 ease-in-out group-hover:text-white/90"
                        >
                            <svg
                                aria-hidden="true"
                                class="h-3.5 w-3.5 shrink-0"
                                fill="none"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.8"
                                viewBox="0 0 24 24"
                            >
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.2 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.34 1.79.66 2.64a2 2 0 0 1-.45 2.11L8.1 9.7a16 16 0 0 0 6.2 6.2l1.23-1.23a2 2 0 0 1 2.11-.45c.85.32 1.74.54 2.64.66A2 2 0 0 1 22 16.92z" />
                            </svg>
                            <a
                                class="min-w-0 break-words no-underline underline-offset-2 hover:underline"
                                :href="toTelHref(item.phone)"
                            >
                                {{ item.phone }}
                            </a>
                        </div>
                        <div
                            v-if="item.to"
                            class="flex min-w-0 items-center gap-2 text-gray-600 dark:text-txt-body transition duration-500 ease-in-out group-hover:text-white/90"
                        >
                            <svg
                                aria-hidden="true"
                                class="h-3.5 w-3.5 shrink-0"
                                fill="none"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.8"
                                viewBox="0 0 24 24"
                            >
                                <path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L10 5" />
                                <path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 1 0 7.07 7.07L14 19" />
                            </svg>
                            <a
                                class="block min-w-0 flex-1 break-all no-underline underline-offset-2 hover:underline"
                                :href="item.to"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {{ toDisplayUrl(item.to) }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <slot name="additional-content"></slot>
        </div>
    </section>
</template>

<script setup lang="ts">
import CHeading from '@/components/ui/CHeading.vue'
import CText from '@/components/ui/CText.vue'

interface OrganizationItem {
    to?: string
    title: string
    email?: string
    phone?: string
    name?: string
}

function toTelHref(phone?: string): string {
    const normalizedPhone = (phone || '').replace(/[^\d+]/g, '')
    return `tel:${normalizedPhone}`
}

// Strip the protocol prefix and trailing slash from the displayed link text (href keeps the full URL)
function toDisplayUrl(url?: string): string {
    return (url || '').replace(/^https?:\/\//, '').replace(/\/+$/, '')
}

withDefaults(
    defineProps<{
        items?: OrganizationItem[]
    }>(),
    {
        items: () => [
            {
                to: 'https://dobrovolnictvo.sk/',
                title: 'Platforma dobrovoľníckych centier a organizácií',
                email: 'scepkova@dobrovolnictvo.sk',
                phone: '+421 908 565 464',
                name: ''
            },
            {
                to: 'http://www.centrumdobrovolnictva.sk/',
                title: 'Centrum dobrovoľníctva Banská Bystrica',
                email: 'cd@centrumdobrovolnictva.sk',
                phone: '+421 908 411 927',
                name: ''
            },
            {
                to: 'https://www.dobrovolnictvotn.sk/',
                title: 'Trenčianske dobrovoľnícke centrum',
                email: 'tndobrovolnickecentrum@gmail.com',
                phone: '+421 951 071 931',
                name: ''
            },
            {
                to: 'https://www.dobrovolnictvoza.sk/',
                title: 'Žilinské dobrovoľnícke centrum',
                email: 'dobrovolnictvo@ozviac.sk',
                phone: '+421 903 262 166',
                name: ''
            },
            {
                to: 'https://www.dobrovolnictvopo.sk/',
                title: 'Prešovské dobrovoľnícke centrum',
                email: 'info@dobrovolnictvopo.sk',
                phone: '+421 910 542 333',
                name: ''
            },
            {
                to: 'https://dobrovolnickecentrumtt.sk/',
                title: 'Dobrovoľnícke centrum Trnavského kraja',
                email: 'info@dobrovolnickecentrumtt.sk',
                phone: '+421 911 525 130',
                name: ''
            },
            {
                to: 'http://www.ncdnitra.sk/',
                title: 'Nitrianske centrum dobrovoľníctva',
                email: 'ncdnitra@gmail.com',
                phone: '+421 907 361 011',
                name: ''
            },
            {
                title: 'GROWNi.sk - technická podpora',
                email: 'dobrovolnictvo@growni.sk',
                phone: '',
                name: ''
            }
        ]
    }
)
</script>
